import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Link2, Loader2, RefreshCw, X } from "lucide-react";

const SHARE_FORMATS = [
    { id: "story", label: "Story", sub: "Instagram / WhatsApp · 1080×1920" },
    { id: "square", label: "Instagram Post", sub: "Square feed · 1080×1080" },
    { id: "facebook", label: "Facebook Post", sub: "Landscape · 1200×630" },
];

/**
 * Branded share-card modal. `fetchCard(slug, format)` must return an object URL.
 * `fetchCaption(slug)` returns {caption, hashtags, ai}. When `refCode` is given
 * (partners), the copy link carries ?ref= and a note is shown.
 */
export function SharePackageModal({ pkg, fetchCard, fetchCaption, shareUrl, refCode, onClose }) {
    const [fmt, setFmt] = useState("story");
    const [imgUrl, setImgUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [capText, setCapText] = useState("");
    const [capLoading, setCapLoading] = useState(true);
    const [capAi, setCapAi] = useState(false);
    const pkgUrl = shareUrl || `${window.location.origin}/package/${pkg.slug}`;

    useEffect(() => {
        let alive = true;
        let obj = null;
        setLoading(true);
        setImgUrl(null);
        fetchCard(pkg.slug, fmt)
            .then((u) => { obj = u; if (alive) { setImgUrl(u); setLoading(false); } })
            .catch(() => { if (alive) { toast.error("Could not generate the share card"); setLoading(false); } });
        return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fmt, pkg.slug]);

    const loadCaption = useCallback(() => {
        if (!fetchCaption) return;
        setCapLoading(true);
        fetchCaption(pkg.slug)
            .then((d) => {
                setCapText(`${d.caption}\n\n${(d.hashtags || []).join(" ")}`);
                setCapAi(!!d.ai);
                setCapLoading(false);
            })
            .catch(() => { toast.error("Could not write the caption"); setCapLoading(false); });
    }, [fetchCaption, pkg.slug]);

    useEffect(() => { loadCaption(); }, [loadCaption]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(pkgUrl);
            toast.success("Package link copied");
        } catch { toast.error("Could not copy"); }
    };

    const copyCaption = async () => {
        try {
            await navigator.clipboard.writeText(capText);
            toast.success("Caption + hashtags copied");
        } catch { toast.error("Could not copy"); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4" data-testid="share-modal">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="font-display font-black text-lg text-navy">Share “{pkg.name || pkg.title}”</h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                            {refCode
                                ? <>Branded card with your referral code <span className="font-bold text-saffron">{refCode}</span> — the QR & link credit every enquiry to you.</>
                                : "Branded card with your logo, price, highlights & QR code to the trip page."}
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="share-modal-close" className="text-stone-400 hover:text-navy"><X className="h-5 w-5" /></button>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                    {SHARE_FORMATS.map((f) => (
                        <button key={f.id} onClick={() => setFmt(f.id)} data-testid={`share-fmt-${f.id}`}
                            className={`rounded-xl border p-2.5 text-left transition-colors ${fmt === f.id ? "border-saffron bg-saffron/5" : "border-stone-200 hover:border-stone-300"}`}>
                            <p className="text-xs font-black text-navy">{f.label}</p>
                            <p className="text-[10px] text-stone-500 mt-0.5">{f.sub}</p>
                        </button>
                    ))}
                </div>

                <div className="mt-4 flex items-center justify-center rounded-xl bg-stone-100 p-4 min-h-[300px]" data-testid="share-card-preview">
                    {loading
                        ? <Loader2 className="h-6 w-6 animate-spin text-saffron" />
                        : imgUrl && <img src={imgUrl} alt="share card" className="max-h-[380px] w-auto rounded-lg shadow-md" />}
                </div>

                <div className="mt-4 flex items-center gap-2">
                    {imgUrl && (
                        <a href={imgUrl} download={`ghumnechaley-${pkg.slug}-${fmt}${refCode ? "-" + refCode : ""}.png`} data-testid="share-download-btn"
                            className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-white px-4 py-2 text-[11px] font-black uppercase tracking-wider hover:brightness-95">
                            <Download className="h-3.5 w-3.5" /> Download PNG
                        </a>
                    )}
                    <button onClick={copyLink} data-testid="share-copy-link-btn"
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-navy hover:border-navy">
                        <Link2 className="h-3.5 w-3.5" /> Copy trip link
                    </button>
                </div>

                {fetchCaption && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between">
                            <p className="text-[11px] font-black uppercase tracking-wider text-stone-500">
                                Caption & hashtags {capAi && <span className="text-saffron normal-case font-bold">· AI-written</span>}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={loadCaption} disabled={capLoading} data-testid="share-caption-regen"
                                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-navy hover:text-saffron disabled:opacity-40">
                                    <RefreshCw className={`h-3 w-3 ${capLoading ? "animate-spin" : ""}`} /> Rewrite
                                </button>
                                <button onClick={copyCaption} disabled={capLoading || !capText} data-testid="share-copy-caption-btn"
                                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-navy hover:text-saffron disabled:opacity-40">
                                    <Copy className="h-3 w-3" /> Copy caption
                                </button>
                            </div>
                        </div>
                        <div className="mt-1.5 rounded-xl border border-stone-200 bg-stone-50">
                            {capLoading
                                ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-saffron" /></div>
                                : <textarea value={capText} onChange={(e) => setCapText(e.target.value)} rows={8}
                                    data-testid="share-caption-editor"
                                    className="w-full rounded-xl bg-transparent p-3 text-xs text-stone-700 leading-relaxed focus:outline-none resize-y" />}
                        </div>
                        <p className="mt-1 text-[10px] text-stone-400">Edit freely before copying — your referral code and tracked link are already inside.</p>
                    </div>
                )}
                <p className="mt-3 text-[11px] text-stone-400 leading-relaxed">
                    How to post: download the card, then upload it on Instagram/Facebook with the trip link in the caption
                    (for Instagram also add the link in bio). The QR on the card opens the package page directly.
                    {refCode && " Your referral code is printed on the card and embedded in the QR — anyone booking through it is credited to you."}
                </p>
            </div>
        </div>
    );
}
